import { isRawSpeakerLabel } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useSpeakerHypothesisActions } from "../../hooks/useSpeakerHypothesisActions"
import { useSpeakerRosterSearch } from "../../hooks/useSpeakerRosterSearch"
import {
  type ICompanySpeaker,
  linkSpeakerEntity,
  mergeSpeakerEntities,
  rejectSpeakerEntity,
  renameSpeaker,
  renameSpeakerEntity,
  unmergeSpeakerEntity,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyWithUndo } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { applySpeakerOptimistic } from "../../lib/speaker-optimistic"
import {
  buildMergeOptions,
  type ISpeakerMergeOption,
  type ISpeakerVM,
  speakerDisplayLabel,
  speakerEntityId,
  speakerRosterKey,
} from "../../lib/speaker-vm"
import type { IMeetingDetail } from "../../lib/types/meeting"
import { useMeetingPlayer } from "../MeetingSummary/MeetingPlayerContext"
import type { TSpeakerRosterChoice } from "../SpeakerRosterPicker/interfaces"
import {
  buildEvidenceLine,
  clipSampleWindow,
  conflictDisplayLabel,
  conflictWithLabelFor,
  duplicateNamePeers,
  needsAName,
  sampleDurationSeconds,
  sortSpeakersByTalk,
} from "./helpers"
import type { IProps } from "./interfaces"
import { WhoSpokePanelView } from "./WhoSpokePanelView"
import type { IRowModel } from "./WhoSpokePanelView/interfaces"

const FRESH_MS = 2500

const SUGGESTION_ID_PREFIX = "suggestion:"

function rowKey(speaker: ISpeakerVM): string {
  return speakerEntityId(speaker) ?? speaker.label ?? speaker.displayName
}

/** The row's voice-match, shaped like a roster person so the picker can pin it. */
function suggestionAsPerson(speaker: ISpeakerVM | undefined): ICompanySpeaker | null {
  const suggestion = speaker?.suggestion
  if (!suggestion || suggestion.name.trim().length === 0) return null
  const speakerId = suggestion.speakerId
  const id = speakerId != null && speakerId.length > 0 ? speakerId : `${SUGGESTION_ID_PREFIX}${suggestion.name}`
  return {
    id,
    company_id: "",
    name: suggestion.name,
    title: null,
    color: null,
    email: null,
    created_at: "",
    updated_at: "",
    recognized_meeting_count: 0,
    sample_count: 0,
    last_seen_at: null,
  }
}

export function WhoSpokePanel({
  meetingId,
  speakers,
  onEditSpeaker,
  onConfirmSuggestion,
  onRejectSuggestion,
  suggestionPending,
  focusFirstUnnamedSignal = 0,
}: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const player = useMeetingPlayer()
  const hypothesis = useSpeakerHypothesisActions(meetingId)

  const [pickerKey, setPickerKey] = useState<string | null>(null)
  const [pickerQuery, setPickerQuery] = useState("")
  const [pickerUsing, setPickerUsing] = useState(false)
  const externalSuggestions = onConfirmSuggestion !== undefined || onRejectSuggestion !== undefined
  const [pickerError, setPickerError] = useState<string | null>(null)
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set())
  const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map())
  const [freshKeys, setFreshKeys] = useState<Set<string>>(new Set())
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const freshTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const rosterSearch = useSpeakerRosterSearch(pickerQuery, pickerKey !== null)

  const sorted = useMemo(() => sortSpeakersByTalk(speakers), [speakers])
  const unnamed = useMemo(() => sorted.filter(needsAName), [sorted])

  const markFresh = useCallback((key: string): void => {
    const prev = freshTimers.current.get(key)
    if (prev) clearTimeout(prev)
    setFreshKeys((current) => new Set(current).add(key))
    freshTimers.current.set(
      key,
      setTimeout(() => {
        setFreshKeys((current) => {
          const next = new Set(current)
          next.delete(key)
          return next
        })
        freshTimers.current.delete(key)
      }, FRESH_MS),
    )
  }, [])

  const setRowError = useCallback((key: string, message: string | null): void => {
    setRowErrors((current) => {
      const next = new Map(current)
      if (message) next.set(key, message)
      else next.delete(key)
      return next
    })
  }, [])

  const withPending = useCallback(
    async (pendingKey: string, errorKey: string, work: () => Promise<void>): Promise<boolean> => {
      setPendingKeys((prev) => new Set(prev).add(pendingKey))
      try {
        await work()
        return true
      } catch (err) {
        setRowError(errorKey, extractErrorMessage(err))
        return false
      } finally {
        setPendingKeys((prev) => {
          const next = new Set(prev)
          next.delete(pendingKey)
          return next
        })
      }
    },
    [setRowError],
  )

  const patchMeetingSpeakers = useCallback(
    (patch: Parameters<typeof applySpeakerOptimistic>[1]): IMeetingDetail | undefined => {
      const meetingKey = queryKeys.meeting(orgId, meetingId)
      const previous = queryClient.getQueryData<IMeetingDetail>(meetingKey)
      if (previous?.speakers) {
        queryClient.setQueryData<IMeetingDetail>(meetingKey, {
          ...previous,
          speakers: applySpeakerOptimistic(previous.speakers, patch),
        })
      }
      return previous
    },
    [meetingId, orgId, queryClient],
  )

  const restoreMeeting = useCallback(
    (previous: IMeetingDetail | undefined): void => {
      if (previous === undefined) return
      queryClient.setQueryData(queryKeys.meeting(orgId, meetingId), previous)
    },
    [meetingId, orgId, queryClient],
  )

  const invalidate = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
  }, [meetingId, orgId, queryClient])

  const closePicker = useCallback((): void => {
    setPickerKey(null)
    setPickerQuery("")
    setPickerError(null)
    setPickerUsing(false)
  }, [])

  const openPicker = useCallback((speaker: ISpeakerVM): void => {
    setPickerKey(rowKey(speaker))
    setPickerQuery("")
    setPickerError(null)
    setFocusKey(rowKey(speaker))
  }, [])

  const handleNeedsNameClick = useCallback((): void => {
    const first = unnamed[0]
    if (!first) return
    const key = rowKey(first)
    setFocusKey(key)
    const el = rowRefs.current.get(key)
    el?.scrollIntoView({ behavior: "smooth", block: "center" })
    if (first.status === "unidentified") openPicker(first)
  }, [unnamed, openPicker])

  const seenFocusSignal = useRef(0)
  useEffect(() => {
    if (focusFirstUnnamedSignal === 0 || focusFirstUnnamedSignal === seenFocusSignal.current) return
    const first = unnamed[0]
    if (!first) return
    seenFocusSignal.current = focusFirstUnnamedSignal
    const key = rowKey(first)
    setFocusKey(key)
    openPicker(first)
    const id = window.setTimeout(() => {
      const root = rowRefs.current.get(key)
      root?.scrollIntoView({ behavior: "smooth", block: "center" })
      root?.querySelector<HTMLInputElement>('[data-testid="speaker-roster-input"]')?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [focusFirstUnnamedSignal, unnamed, openPicker])

  const handlePlaySample = useCallback(
    (speaker: ISpeakerVM): void => {
      const window = clipSampleWindow(speaker.sampleWindow ?? speaker.longestSegment)
      if (!window || !player.available) return
      player.playWindow(window.start, window.end)
    },
    [player],
  )

  const handleConfirm = useCallback(
    async (speaker: ISpeakerVM): Promise<void> => {
      const suggestion = speaker.suggestion
      if (!suggestion || !speaker.label) return
      const key = rowKey(speaker)
      setRowError(key, null)
      if (externalSuggestions) {
        onConfirmSuggestion?.(speaker.label, suggestion.name)
        markFresh(key)
        return
      }
      const ok = await hypothesis.onAction(speaker.label, suggestion.name, "confirm")
      if (ok) markFresh(key)
    },
    [externalSuggestions, hypothesis, markFresh, onConfirmSuggestion, setRowError],
  )

  const applyReject = useCallback(
    async (speaker: ISpeakerVM): Promise<void> => {
      const key = rowKey(speaker)
      setRowError(key, null)
      const display = speakerDisplayLabel(speaker, i18n)
      if (speaker.status === "suggested" && speaker.suggestion && speaker.label) {
        const suggestionName = speaker.suggestion.name
        const label = speaker.label
        if (externalSuggestions) {
          onRejectSuggestion?.(label, suggestionName)
          markFresh(key)
          notifyWithUndo({
            message: i18n._(msg`${suggestionName} was not applied.`),
            onUndo: () => {
              onConfirmSuggestion?.(label, suggestionName)
            },
          })
          return
        }
        const ok = await hypothesis.onAction(label, suggestionName, "reject")
        if (ok) {
          markFresh(key)
          notifyWithUndo({
            message: i18n._(msg`${suggestionName} was not applied.`),
            onUndo: () => {
              void hypothesis.onAction(label, suggestionName, "confirm")
            },
          })
        }
        return
      }
      const entityId = speakerEntityId(speaker)
      if (!company || entityId === undefined) return
      const pendingKey = `reject|${entityId}`
      const previousName = speaker.displayName
      const previousSpeakerId = speaker.speakerId
      const previous = patchMeetingSpeakers({
        kind: "reject-named",
        entityId,
        label: speaker.label,
      })
      const ok = await withPending(pendingKey, key, async () => {
        await rejectSpeakerEntity(company, meetingId, entityId, { revokeVoice: true })
      })
      if (ok) {
        invalidate()
        markFresh(key)
        notifyWithUndo({
          message: i18n._(msg`${display} is unnamed again.`),
          onUndo: () => {
            void (async (): Promise<void> => {
              const renamed = await renameSpeakerEntity(company, meetingId, entityId, previousName)
              if (renamed.ok && previousSpeakerId) {
                await linkSpeakerEntity(company, meetingId, entityId, previousSpeakerId)
              }
              invalidate()
            })()
          },
        })
      } else {
        restoreMeeting(previous)
      }
    },
    [
      company,
      externalSuggestions,
      hypothesis,
      i18n,
      invalidate,
      markFresh,
      meetingId,
      onConfirmSuggestion,
      onRejectSuggestion,
      patchMeetingSpeakers,
      restoreMeeting,
      setRowError,
      withPending,
    ],
  )

  const handleReject = useCallback(
    (speaker: ISpeakerVM): void => {
      const display = speakerDisplayLabel(speaker, i18n)
      const isSuggestion = speaker.status === "suggested" && Boolean(speaker.suggestion)
      const talk = speaker.talkPercent
      modals.openConfirmModal({
        title: i18n._(msg`Not them?`),
        children: isSuggestion ? (
          <Text size="sm">
            {talk != null ? (
              <Trans>
                Don't use {speaker.suggestion?.name} for this speaker? They become Speaker {speaker.displayIndex} (
                {talk}% of the talk). You can pick someone else afterwards. You can Undo for a few seconds after.
              </Trans>
            ) : (
              <Trans>
                Don't use {speaker.suggestion?.name} for this speaker? They become Speaker {speaker.displayIndex}. You
                can pick someone else afterwards. You can Undo for a few seconds after.
              </Trans>
            )}
          </Text>
        ) : (
          <Text size="sm">
            {talk != null ? (
              <Trans>
                {display} will be unnamed ({talk}% of the talk). The voice sample for this meeting will be removed. You
                can Undo for a few seconds after.
              </Trans>
            ) : (
              <Trans>
                {display} will be unnamed. The voice sample for this meeting will be removed. You can Undo for a few
                seconds after.
              </Trans>
            )}
          </Text>
        ),
        labels: { confirm: i18n._(msg`Not them`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => {
          void applyReject(speaker)
        },
      })
    },
    [applyReject, i18n],
  )

  const handleMerge = useCallback(
    (speaker: ISpeakerVM, option: ISpeakerMergeOption): void => {
      const entityId = speakerEntityId(speaker)
      if (!company || entityId === undefined || option.entityId === undefined) return
      const display = speakerDisplayLabel(speaker, i18n)
      const talk = speaker.talkPercent
      const segments = speaker.segmentCount
      const target = option.displayName
      const sourceLabel = speaker.label
      modals.openConfirmModal({
        title: i18n._(msg`Merge speakers?`),
        children: (
          <Text size="sm">
            {segments > 0 && talk != null ? (
              <Trans>
                Merge {display} into {target}? {segments} transcript segments ({talk}% of the talk) move onto {target}.
                Voice samples combine. You can Undo for a few seconds after.
              </Trans>
            ) : segments > 0 ? (
              <Trans>
                Merge {display} into {target}? {segments} transcript segments move onto {target}. Voice samples combine.
                You can Undo for a few seconds after.
              </Trans>
            ) : talk != null ? (
              <Trans>
                Merge {display} into {target}? {display} is {talk}% of the talk. Their transcript lines and voice
                samples combine. You can Undo for a few seconds after.
              </Trans>
            ) : (
              <Trans>
                Merge {display} into {target}? Their transcript lines and voice samples combine. You can Undo for a few
                seconds after.
              </Trans>
            )}
          </Text>
        ),
        labels: { confirm: i18n._(msg`Merge`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "warning" },
        onConfirm: () => {
          const key = rowKey(speaker)
          setRowError(key, null)
          const pendingKey = `merge|${entityId}`
          const targetId = option.entityId ?? ""
          void withPending(pendingKey, key, async () => {
            await mergeSpeakerEntities(company, meetingId, targetId, [entityId])
          }).then((ok) => {
            if (!ok) return
            invalidate()
            notifyWithUndo({
              message: i18n._(msg`Merged ${display} into ${option.displayName}.`),
              onUndo: () => {
                void (async (): Promise<void> => {
                  await unmergeSpeakerEntity(company, meetingId, entityId, sourceLabel ? [sourceLabel] : undefined)
                  invalidate()
                })()
              },
            })
          })
        },
      })
    },
    [company, i18n, invalidate, meetingId, setRowError, withPending],
  )

  const handleMergeIntoConflict = useCallback(
    async (speaker: ISpeakerVM): Promise<void> => {
      const conflictLabel = conflictWithLabelFor(speaker, speakers)
      if (!conflictLabel) return
      const peer = speakers.find((s) => s.label === conflictLabel)
      if (!peer) return
      const option: ISpeakerMergeOption = {
        entityId: speakerEntityId(peer),
        displayName: speakerDisplayLabel(peer, i18n),
        farEnd: peer.farEnd,
      }
      await handleMerge(speaker, option)
    },
    [handleMerge, i18n, speakers],
  )

  const handlePickerCommit = useCallback(
    async (choice: TSpeakerRosterChoice): Promise<void> => {
      if (!company || pickerKey === null || pickerUsing) return
      const speaker = speakers.find((s) => rowKey(s) === pickerKey)
      if (!speaker) return
      const entityId = speakerEntityId(speaker)
      const person = choice.kind === "person" ? choice.person : null
      const linkedPerson = person !== null && !person.id.startsWith(SUGGESTION_ID_PREFIX) ? person : null
      const name = (linkedPerson?.name ?? person?.name ?? (choice.kind === "create" ? choice.name : "")).trim()
      if (name.length === 0) return
      const previousName = speaker.displayName
      const previousSpeakerId = speaker.speakerId
      const wasPlaceholder =
        speaker.status === "unidentified" || speaker.status === "suggested" || isRawSpeakerLabel(speaker.displayName)
      const namedLabel = speakerDisplayLabel(speaker, i18n)
      const key = rowKey(speaker)
      setPickerError(null)
      setPickerUsing(true)
      const previous = linkedPerson
        ? patchMeetingSpeakers({
            kind: "link",
            entityId,
            label: speaker.label,
            name,
            speakerId: linkedPerson.id,
            color: linkedPerson.color,
          })
        : patchMeetingSpeakers({
            kind: "rename",
            entityId,
            label: speaker.label,
            name,
          })
      try {
        if (linkedPerson && entityId !== undefined) {
          await linkSpeakerEntity(company, meetingId, entityId, linkedPerson.id)
        } else if (entityId !== undefined) {
          const result = await renameSpeakerEntity(company, meetingId, entityId, name)
          if (!result.ok) {
            restoreMeeting(previous)
            setPickerError(
              i18n._(
                msg`"${result.mergeCandidate.displayName}" already exists — merge into them from Merge with… instead.`,
              ),
            )
            setPickerUsing(false)
            return
          }
        } else {
          await renameSpeaker(company, meetingId, speakerRosterKey(speaker), name)
        }
        invalidate()
        markFresh(key)
        closePicker()
        notifyWithUndo({
          message: i18n._(msg`Named ${namedLabel} as ${name}.`),
          onUndo: () => {
            void (async (): Promise<void> => {
              try {
                if (entityId !== undefined && wasPlaceholder) {
                  await rejectSpeakerEntity(company, meetingId, entityId, { revokeVoice: true })
                } else if (entityId !== undefined) {
                  const renamed = await renameSpeakerEntity(company, meetingId, entityId, previousName)
                  if (renamed.ok && previousSpeakerId) {
                    await linkSpeakerEntity(company, meetingId, entityId, previousSpeakerId)
                  }
                } else {
                  await renameSpeaker(company, meetingId, name, previousName)
                }
                invalidate()
              } catch (err) {
                setRowError(key, extractErrorMessage(err))
              }
            })()
          },
        })
      } catch (err) {
        restoreMeeting(previous)
        setPickerError(extractErrorMessage(err))
      } finally {
        setPickerUsing(false)
      }
    },
    [
      closePicker,
      company,
      i18n,
      invalidate,
      markFresh,
      meetingId,
      patchMeetingSpeakers,
      pickerKey,
      pickerUsing,
      restoreMeeting,
      setRowError,
      speakers,
    ],
  )

  const rows: IRowModel[] = useMemo(() => {
    return sorted.map((speaker) => {
      const key = rowKey(speaker)
      const suggestion = speaker.suggestion
      const conflictLabel = conflictWithLabelFor(speaker, speakers)
      const conflictPeer = conflictLabel ? conflictDisplayLabel(conflictLabel, speakers, i18n) : null
      const window = clipSampleWindow(speaker.sampleWindow ?? speaker.longestSegment)
      let statusText = ""
      if (speaker.status === "suggested") statusText = i18n._(msg`suggested`)
      else if (speaker.status === "unidentified") {
        statusText = suggestion ? i18n._(msg`suggested`) : i18n._(msg`needs a name · no suggestion`)
      } else if (speaker.voiceSaved) statusText = i18n._(msg`voice saved`)
      const runnerUp = suggestion?.runnerUp
      return {
        speaker,
        displayLabel: speakerDisplayLabel(speaker, i18n),
        statusText,
        confirmedByYou: speaker.confirmedBy === "user",
        voiceSaved: speaker.voiceSaved === true,
        fresh: freshKeys.has(key),
        focused: focusKey === key || pickerKey === key,
        evidenceLine: buildEvidenceLine(suggestion?.evidence),
        runnerUpLine: runnerUp
          ? i18n._(msg`Or ${runnerUp.name} · ${Math.round(runnerUp.score * 100)}% confidence`)
          : null,
        conflictLine: conflictPeer
          ? i18n._(msg`Same voice as ${conflictPeer} in two places. Listen before naming, or merge them.`)
          : null,
        conflictPeerLabel: conflictPeer,
        duplicateLine:
          duplicateNamePeers(speaker, speakers).length > 0
            ? i18n._(
                msg`Another speaker is also named ${speakerDisplayLabel(speaker, i18n)}. Merge them, Not them, or rename one.`,
              )
            : null,
        mergeOptions: buildMergeOptions(speakers, speaker.displayName),
        sampleSeconds: sampleDurationSeconds(window),
        pickerOpen: pickerKey === key,
        confirmPending: Boolean(
          suggestion &&
            speaker.label &&
            (externalSuggestions ? suggestionPending : hypothesis.pending)?.has(
              `${speaker.label}|${suggestion.name}|confirm`,
            ),
        ),
        rejectPending:
          Boolean(
            suggestion &&
              speaker.label &&
              (externalSuggestions ? suggestionPending : hypothesis.pending)?.has(
                `${speaker.label}|${suggestion.name}|reject`,
              ),
          ) || pendingKeys.has(`reject|${speakerEntityId(speaker) ?? ""}`),
        mergePending: pendingKeys.has(`merge|${speakerEntityId(speaker) ?? ""}`),
        error: rowErrors.get(key) ?? null,
      }
    })
  }, [
    externalSuggestions,
    focusKey,
    freshKeys,
    hypothesis.pending,
    i18n,
    pendingKeys,
    pickerKey,
    rowErrors,
    sorted,
    speakers,
    suggestionPending,
  ])

  if (speakers.length === 0) return null

  return (
    <WhoSpokePanelView
      rows={rows}
      unnamedCount={unnamed.length}
      speakerCount={sorted.length}
      showSample={player.available}
      pickerQuery={pickerQuery}
      pickerPeople={rosterSearch.people}
      pickerLoading={rosterSearch.isFetching}
      pickerSuggested={suggestionAsPerson(speakers.find((speaker) => rowKey(speaker) === pickerKey))}
      pickerUsing={pickerUsing}
      pickerError={pickerError}
      onNeedsNameClick={handleNeedsNameClick}
      onPlaySample={handlePlaySample}
      onConfirm={(speaker) => void handleConfirm(speaker)}
      onReject={(speaker) => void handleReject(speaker)}
      onPickSomeoneElse={openPicker}
      onNameSpeaker={openPicker}
      onEdit={onEditSpeaker}
      onMerge={(speaker, option) => void handleMerge(speaker, option)}
      onMergeIntoConflict={(speaker) => void handleMergeIntoConflict(speaker)}
      onPickerQueryChange={setPickerQuery}
      onPickerCommit={(choice) => void handlePickerCommit(choice)}
      onPickerCancel={closePicker}
      rowRefs={rowRefs}
    />
  )
}
